/**
 * AP-773 §3.2 — the Simpson / Doppler sibling results a viewer action rewrites.
 *
 * The Simpson disk overlay and the Doppler baseline overlay draw from these
 * Redux results (keyed by annotation UID), not from the annotation's data, so
 * a step restores them alongside the geometry or the overlays keep the
 * reshaped disks / baseline while the contour reverts. The annotation →
 * measurement mapping rides along too: clearing a deleted annotation drops
 * it, and the worksheet's selected row derives from it (R17).
 */
import type { Dispatch } from '@reduxjs/toolkit';
import type { SimpsonResult } from '@/utils/simpsonMethod/types';
import type { SpectralDopplerResult } from '@/models/spectralDoppler';
import {
  addSimpsonResult,
  addSpectralDopplerResult,
  mapAnnotationToMeasurement,
  removeSimpsonResult,
  removeSpectralDopplerResult,
  selectAnnotationToMeasurement,
  selectSimpsonResults,
  selectSpectralDopplerResults,
  unmapAnnotation,
} from '@/state/slices/measurementSession';
import type { RootState } from '@/state/store';

export interface SessionResultsSnapshot {
  simpson: SimpsonResult | null;
  spectral: SpectralDopplerResult | null;
  /** canonicalMeasurement the annotation is mapped to, if any. */
  canonical: string | null;
}

export function snapshotSessionResults(
  state: RootState,
  annotationUID: string
): SessionResultsSnapshot {
  return {
    simpson: selectSimpsonResults(state)[annotationUID] ?? null,
    spectral: selectSpectralDopplerResults(state)[annotationUID] ?? null,
    canonical: selectAnnotationToMeasurement(state)[annotationUID] ?? null,
  };
}

export function sameSessionResults(
  a: SessionResultsSnapshot,
  b: SessionResultsSnapshot
): boolean {
  return (
    a.simpson === b.simpson &&
    a.spectral === b.spectral &&
    a.canonical === b.canonical
  );
}

export function restoreSessionResults(
  dispatch: Dispatch,
  annotationUID: string,
  snapshot: SessionResultsSnapshot
): void {
  dispatch(
    snapshot.simpson
      ? addSimpsonResult(snapshot.simpson)
      : removeSimpsonResult(annotationUID)
  );
  dispatch(
    snapshot.spectral
      ? addSpectralDopplerResult(snapshot.spectral)
      : removeSpectralDopplerResult(annotationUID)
  );
  dispatch(
    snapshot.canonical
      ? mapAnnotationToMeasurement({
          annotationUID,
          canonicalMeasurement: snapshot.canonical,
        })
      : unmapAnnotation(annotationUID)
  );
}
